import { CheckCircleIcon } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Field } from '@/modules/primitives3D/editorShell';
import { FieldLabel, FlowTitle } from '@/components/ui/text';
import { DEVICE_LABELS } from '../deviceAvailability';
import type { PublishDevice } from '../types';

const DEVICE_ORDER: PublishDevice[] = ['app', 'appless', 'spectacles'];

interface Props {
  devices: PublishDevice[];
  storyNames: string[];
  submitted: boolean;
}

export function ProjectConfirmationStep({ devices, storyNames, submitted }: Props) {
  const shareDevices = DEVICE_ORDER.filter((d) => devices.includes(d));

  if (submitted) {
    const one = storyNames.length === 1;
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-lureo-brand text-lureo-on-brand">
            <CheckCircleIcon className="h-8 w-8" />
          </div>
          <FlowTitle as="h3" className="mt-4">
            {storyNames.length} {one ? 'story' : 'stories'} submitted for review
          </FlowTitle>
          <p className="mt-3 font-lureo-font-prose text-lureo-15 text-lureo-ink-subtle leading-normal">
            We'll review {one ? 'it' : 'them'} and let you know within 7 days, by notification and email.
          </p>
          <div className="mt-5 rounded-lureo-radius border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle p-4 text-left">
            <p className="font-lureo-font-ui font-medium text-lureo-14 text-lureo-ink leading-[1.4]">
              Want to try {one ? 'it' : 'them'} before going live?
            </p>
            <p className="mt-1 font-lureo-font-prose text-lureo-13 text-lureo-ink-subtle leading-normal">
              Open a story and use its preview link on the story page to test on your phone right now, no need to
              wait for review.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex items-center">
      <div className="w-full grid grid-cols-2 gap-5 items-stretch">
        <Card>
          <Field>
            <FieldLabel mb>
              Stories ({storyNames.length})
            </FieldLabel>
            <ul className="space-y-1.5 max-h-56 overflow-y-auto">
              {storyNames.map((name, i) => (
                <li
                  key={`${name}-${i}`}
                  className="font-lureo-font-ui font-medium text-lureo-14 text-lureo-ink leading-[1.3] truncate"
                >
                  {name || '—'}
                </li>
              ))}
            </ul>
          </Field>
        </Card>

        <Card>
          <Field>
            <FieldLabel mb>Platforms</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {shareDevices.length === 0 ? (
                <span className="font-lureo-font-ui text-lureo-14 text-lureo-ink-subtlest">—</span>
              ) : (
                shareDevices.map((d) => (
                  <span
                    key={d}
                    className="inline-flex items-center rounded-lureo-radius-pill bg-lureo-brand-ink/10 border-lureo-border-width-medium border-lureo-brand-ink text-lureo-brand-ink py-1 px-3 font-lureo-font-ui text-lureo-12 font-medium leading-[1.3]"
                  >
                    {DEVICE_LABELS[d]}
                  </span>
                ))
              )}
            </div>
          </Field>
          <p className="mt-4 font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-normal">
            These platforms apply to every story you're publishing. Once you submit, we'll review and let you know
            within 7 days.
          </p>
        </Card>
      </div>
    </div>
  );
}
